import * as React from 'react'

import { makeObservable } from 'mobx'
import { observer } from 'mobx-react'

import { IInspectionOptions, IWorkflowStep } from '~/client/graph'
import PermitWorkflowDiagram from '~/client/src/desktop/components/WorkflowDiagrams/PermitWorkflowDiagram/PermitWorkflowDiagram'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import { BULLET } from '~/client/src/shared/utils/usefulStrings'

import BicRulesDraft from '../../BicRulesDraft'
import FormWorkflowSteps from '../FormWorkflowSteps'
import WorkflowStepConfigurator from './components/WorkflowStepConfigurator/WorkflowStepConfigurator'
import WorkflowStepDescriptionsDialog from './components/WorkflowStepDescriptionsDialog'
import WorkflowStore from './Workflow.store'

// localization: translated

interface IProps {
  formTypeId: string
  workflowSteps: IWorkflowStep[]
  inspectionOptions: IInspectionOptions
  isAutoActivationEnabled: boolean
  isUpdating: boolean
  isWorkflowChanged: boolean

  onWorkflowSettingsChange(
    workflowSteps: IWorkflowStep[],
    inspectionOptions: IInspectionOptions,
    isAutoActivationEnabled: boolean,
  ): void
  onStepClick(stepId: string): void

  bicRulesDraft: BicRulesDraft
  permitBallInCourtStore: PermitBallInCourtStore
}

@observer
export default class Workflow extends React.Component<IProps> {
  private readonly store: WorkflowStore = null

  public constructor(props: IProps) {
    super(props)

    this.store = new WorkflowStore(
      props.bicRulesDraft,
      props.permitBallInCourtStore,
      props.formTypeId,
      props.workflowSteps,
      props.inspectionOptions,
      props.isAutoActivationEnabled,
      props.onWorkflowSettingsChange,
    )
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const {
      formTypeId,
      workflowSteps,
      inspectionOptions,
      isAutoActivationEnabled,
    } = this.props
    if (
      formTypeId !== prevProps.formTypeId ||
      workflowSteps.length !== prevProps.workflowSteps.length ||
      workflowSteps !== prevProps.workflowSteps ||
      inspectionOptions !== prevProps.inspectionOptions ||
      isAutoActivationEnabled !== prevProps.isAutoActivationEnabled
    ) {
      this.store.init(
        formTypeId,
        workflowSteps,
        inspectionOptions,
        isAutoActivationEnabled,
      )
    }
  }

  public render() {
    const {
      isUpdating,
      isWorkflowChanged,
      onStepClick,
      permitBallInCourtStore,
    } = this.props
    const {
      getUsersFromExpressions,
      duplicateRule,
      getStepRules,
      tryToRemoveRule,
      removingRuleId,
      newRuleId,
      performRemoveRule,
      resetRemovingRuleId,
      addOrDeleteUserFromRule,
      changeRuleExpression,
      addOrDeleteFieldFromRule,
      addOrDeleteFieldsFromRule,
      applyDiffToRule,
      showNewRuleRow,
      workflowSteps,
      addNewWorkflowStep,
      replaceWorkflowStep,
      showDeleteStepModal,
      changeInspectionOptions,
      inspectionOptions,
      isStepDescrDialogShown,
      showStepDescrDialog,
      hideStepDescrDialog,
      permitTypeId,
      isAutoActivationEnabled,
      toggleAutoActivationState,
      changeNotificationFieldIds,
      changeNotificationFieldIdsBulk,
      changeNotificationUserIds,
      changeNotificationDiff,
      getNotificationUsers,
      changeActions,
      setBicCompletionMode,
      renameRule,
      applyCompanyDiffToRule,
    } = this.store

    return (
      <div className="col permit-type-workflow ml12 mb40">
        {this.deleteStepDialog}
        {this.activationToggleDialog}

        <span className="row mb20 no-white-space-wrap text extra-large bold line-24 no-grow">
          {Localization.translator.workflowSteps}
        </span>

        <div className="row overflow-auto">
          <div className="row x-center">
            <FormWorkflowSteps workflowSteps={workflowSteps} />
          </div>
        </div>

        <span className="row mt20 no-white-space-wrap text extra-large bold line-24 no-grow">
          {Localization.translator.workflowDiagram}
        </span>

        <div className="row overflow-auto">
          <div className="py20 row x-center">
            <PermitWorkflowDiagram
              workflowSteps={workflowSteps}
              isAutoActivationEnabled={isAutoActivationEnabled}
              inspectionFrequency={inspectionOptions?.inspectionFrequency}
              inspectionFrequencyType={
                inspectionOptions?.inspectionFrequencyType
              }
            />
          </div>
        </div>

        <div className="col my10 py10 relative bg-white">
          <div className="row relative">
            <span className="text extra-large bold line-24 no-grow nowrap">
              {Localization.translator.workflowStepConfigurator}
            </span>

            <div
              className="text large bold blue-highlight line-extra-large no-grow nowrap pointer mx15"
              onClick={showStepDescrDialog}
            >
              {Localization.translator.workflowTableText.seeDescr}
            </div>
            <WorkflowStepDescriptionsDialog
              isOpened={isStepDescrDialogShown}
              hideDialog={hideStepDescrDialog}
            />
            <div />
          </div>
        </div>

        <div className="row">
          <div className="py20 row x-center">
            <WorkflowStepConfigurator
              formTypeId={permitTypeId}
              workflowSteps={workflowSteps}
              inspectionOptions={inspectionOptions}
              isUpdating={isUpdating}
              isAutoActivationEnabled={isAutoActivationEnabled}
              isWorkflowChanged={isWorkflowChanged}
              onAddStep={addNewWorkflowStep}
              onReplaceStep={replaceWorkflowStep}
              onRemoveStep={showDeleteStepModal}
              onInspectionChange={changeInspectionOptions}
              onFormSectionClick={onStepClick}
              onAutoActivationChange={toggleAutoActivationState}
              getUsersFromAssignment={getUsersFromExpressions}
              getStepRules={getStepRules}
              onRemoveRuleClick={tryToRemoveRule}
              removingRuleId={removingRuleId}
              newRuleId={newRuleId}
              onRemoveRuleConfirm={performRemoveRule}
              onRemoveRuleCancel={resetRemovingRuleId}
              onDuplicateRuleClick={duplicateRule}
              onChangeUserForRule={addOrDeleteUserFromRule}
              onRuleExpressionChange={changeRuleExpression}
              onAddNewRuleClick={showNewRuleRow}
              onFieldToggleForRule={addOrDeleteFieldFromRule}
              onFieldsToggleForRule={addOrDeleteFieldsFromRule}
              onDiffCommitForRule={applyDiffToRule}
              onBicCompletionModeChange={setBicCompletionMode}
              onRenameRule={renameRule}
              onCompanyDiffCommitForRule={applyCompanyDiffToRule}
              onNotificationFieldsChange={changeNotificationFieldIds}
              onNotificationFieldsBulkChange={changeNotificationFieldIdsBulk}
              onNotificationUserChange={changeNotificationUserIds}
              onNotificationDiffChange={changeNotificationDiff}
              getNotificationUsers={getNotificationUsers}
              permitBallInCourtStore={permitBallInCourtStore}
              onActionsChange={changeActions}
            />
          </div>
        </div>
      </div>
    )
  }

  private get deleteStepDialog(): JSX.Element {
    const {
      isDeleteStepModalShown,
      hideDeleteStepModal,
      confirmRemoveWorkflowStep,
    } = this.store

    return (
      <ConfirmDialog
        useRedConfirmationButton
        title={Localization.translator.workflowConfDescr.deleteWorkflowStep}
        isOpen={isDeleteStepModalShown}
        onCancelClicked={hideDeleteStepModal}
        onDoneClicked={confirmRemoveWorkflowStep}
        doneTitle={Localization.translator.delete}
      >
        <div className="text large pre-line">
          {Localization.translator.workflowConfDescr.removeStepDialogMsg}
        </div>
      </ConfirmDialog>
    )
  }

  private get activationToggleDialog(): JSX.Element {
    const {
      isAutoToggleDialogShown,
      isAutoActivationEnabled,
      hideAllDialogs,
      confirmAutoActivationChange,
    } = this.store

    return (
      <ConfirmDialog
        isOpen={isAutoToggleDialogShown}
        onCancelClicked={hideAllDialogs}
        onDoneClicked={confirmAutoActivationChange}
        doneTitle={Localization.translator.change_verb}
      >
        <div className="text large pre-line">
          {Localization.translator.workflowConfDescr.recurringHasConstr}
          <br />
          <br />
          {BULLET} {Localization.translator.workflowTableText.recurringConstr}
          <br />
          <br />
        </div>
        <div className="text large pre-line">
          {Localization.translator.workflowConfDescr.toggleDialogMsg(
            isAutoActivationEnabled,
          )}
        </div>
      </ConfirmDialog>
    )
  }
}
